/**
 * WHAT A PROTOCOL SECTION HEADING IS — one rule, shared.
 *
 * Two pieces of code ask this question: the citation index the content pass
 * offers the model (crf/contentPassPolicy.buildProtocolSectionIndex) and the
 * section spans the CRF prompts select text from
 * (crf/protocolTextForPrompt.protocolSpans). Until 2026-10-19 they answered it
 * with different rules. The index read only "8.1.3 Title" (no trailing dot)
 * and accepted dates, so on NCT03595553 (207,337 characters, headings printed
 * "8.1.3. \tSuspected Recurrence") it found 13 "headings", 9 of them
 * amendment dates ("28 SEP 2017" as section 28, "SEP 2017"), and 32 of 39
 * note citations were dates (backlog 21 y). The selection already read both
 * styles through a numbering chain. Both now read headings here.
 *
 * The rule, all structure, no word list beyond the twelve month names:
 *
 *   1. A candidate is a line that starts with a section number, plain
 *      ("8.1.3 Title") or dotted ("8.1.3. Title"), then a Title-case or
 *      upper-case title of 3 to 81 characters that does not end in a period.
 *      CRLF is tolerated (the "\r" is trimmed off the title).
 *   2. A date is never a candidate: a title that starts with a month name
 *      and a number ("28 SEP 2017", "12 March 2019") or with a whole date
 *      ("2.0 12 March 2019 Initial release"), a three-part number with a
 *      zero-padded part ("12.03.19") or any number with a year-sized part
 *      ("12.03.2019", "2019.03"). Section numbers never reach 1900; "01
 *      INTRODUCTION" and "1.01 Background" are sections (some templates pad).
 *   3. A line ending in a dot leader and a page number is a TOC line, never a
 *      heading, even where the TOC is too short to be found as a region.
 *   4. The TOC region (findTocRegion) is left out.
 *   5. The document's headings are the longer of the two numbering CHAINS
 *      (plain or dotted): the longest sequence of candidates whose numbers
 *      could follow one another. A numbered list item, a table row or a page
 *      footer cannot extend the chain and is dropped.
 *
 * The citation index adds one thing to (5): a multi-level candidate of either
 * style that is off the chain, whose number the chain does not already hold
 * (citationHeadings). A synopsis cites sections out of order ("6.1.2" between
 * "5.1" and "5.3"), and a sponsor template can print one subsection in the
 * other style ("9.4.1 \tEfficacy Analyses" among "9.4." headings). A
 * single-number line off the chain is not added: those are list items, stool
 * charts and street addresses (measured on the eight reference protocols).
 *
 * PURE. No model, no Firestore.
 */

export interface TextRange { start: number; end: number }

export interface HeadingCandidate {
  /** "8.1.3", always without a trailing dot. */
  number: string;
  title: string;
  /** Character offset of the heading line in the text. */
  start: number;
}

// ── The table of contents ──────────────────────────────────────────────────

/** "Synopsis ........ 8", "1.1 Synopsis …… 8": a dot (or ellipsis) leader then a page number. */
export const LEADER_LINE = /(?:\.[ \t]*){4,}[ \t]*\d{1,4}[ \t]*$|…{2,}[ \t]*\d{1,4}[ \t]*$/;
/** "1.1 \tSynopsis \t8": a numbered heading ending in a tab or wide gap then a page number. */
const NUMBERED_PAGE_LINE = /^[ \t]*\d+(?:\.\d+)*\.?[ \t]+[A-Za-z][^\n]*?(?:\t|[ ]{2,})[ \t]*\d{1,4}[ \t]*$/;
/** The TOC is near the start: it must begin in the first share of the text. */
const TOC_SEARCH_SHARE = 0.35;
/** Lines between two TOC lines that still belong to one run (page breaks, wrapped titles). */
const TOC_MAX_GAP_LINES = 10;
/** Fewer TOC-shaped lines than this is not a table of contents. */
const TOC_MIN_LINES = 5;

/**
 * The table of contents, as a character range, or null. Found by shape, not
 * by the words "Table of Contents": the longest dense run of lines ending in
 * a dot leader and a page number (or a numbered heading and a page number)
 * that starts in the first 35% of the text. A "TABLE OF CONTENTS" heading
 * just above the run is included. Lists of tables and figures that follow
 * the TOC are the same shape and join the run.
 */
export function findTocRegion(text: string): TextRange | null {
  const src = String(text ?? '');
  if (!src) return null;
  const limit = Math.floor(src.length * TOC_SEARCH_SHARE);
  const lines: Array<{ start: number; end: number; toc: boolean; raw: string }> = [];
  let pos = 0;
  for (const raw of src.split('\n')) {
    const start = pos;
    const end = pos + raw.length;
    pos = end + 1;
    // A CRLF text keeps "\r" at each line end; the shapes are tested without it.
    const line = raw.endsWith('\r') ? raw.slice(0, -1) : raw;
    lines.push({ start, end, toc: LEADER_LINE.test(line) || NUMBERED_PAGE_LINE.test(line), raw });
  }
  type Run = { first: number; last: number; count: number };
  const runs: Run[] = [];
  let cur: Run | null = null;
  for (let i = 0; i < lines.length; i++) {
    if (!lines[i].toc) continue;
    if (cur && i - cur.last <= TOC_MAX_GAP_LINES + 1) { cur.last = i; cur.count++; continue; }
    if (cur) runs.push(cur);
    cur = { first: i, last: i, count: 1 };
  }
  if (cur) runs.push(cur);
  const eligible = runs.filter((r) => r.count >= TOC_MIN_LINES && lines[r.first].start <= limit);
  if (!eligible.length) return null;
  const best = eligible.reduce((a, b) => (b.count > a.count ? b : a));
  let first = best.first;
  for (let k = 1; k <= 3 && first - k >= 0; k++) {
    if (/^\s*(table of contents|contents)\s*$/i.test(lines[first - k].raw)) { first -= k; break; }
  }
  const endLine = lines[best.last];
  return { start: lines[first].start, end: Math.min(src.length, endLine.end + 1) };
}

/** Is this offset inside the TOC region? */
export function inToc(offset: number, toc: TextRange | null): boolean {
  return !!toc && offset >= toc.start && offset < toc.end;
}

// ── Candidates ─────────────────────────────────────────────────────────────

/** "8.1.3 Suspected Recurrence". */
const PLAIN_HEADING = /^[ \t]*(\d+(?:\.\d+)*)[ \t]+([A-Z][^\n]{2,80}?)[ \t]*$/gm;
/** "8.1.3. \tSuspected Recurrence": the number printed with a trailing dot. */
const DOTTED_HEADING = /^[ \t]*(\d+(?:\.\d+)*)\.[ \t]+([A-Z][^\n]{2,80}?)[ \t]*$/gm;

/**
 * A title that is the rest of a date: a month name, then a year or a day
 * ("SEP 2017", "March 2019", "Sept. 2020", "Jul, 18"). "Mayo score",
 * "Febrile neutropenia" and "May Visit" are headings, not dates.
 */
const MONTH = '(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)';
const MONTH_THEN_NUMBER = new RegExp(`^${MONTH}\\.?,?[ \\t]+\\d{1,4}\\b`, 'i');
/**
 * A title that STARTS with a full date: a version-history row "2.0 12 March
 * 2019 Initial release", "2.0 12-Mar-2019", "1.1 12/03/2019", "3.0
 * 2019-03-12". Its number is a document version, not a section (checker,
 * 2026-10-19: these rows still verified "Section 2.0" and bounded sections in
 * sectionVerify, whose numbered-line pattern takes any title).
 */
const TITLE_STARTS_WITH_DATE = new RegExp(
  `^(?:\\d{1,2}[ \\t\\-./]+${MONTH}\\.?,?[ \\t\\-./]*\\d{2,4}\\b|\\d{1,4}[/\\-.]\\d{1,2}[/\\-.]\\d{2,4}\\b)`,
  'i',
);

/**
 * Is this "heading" a date? "28 SEP 2017" (number 28, title "SEP 2017"),
 * "12 March 2019", "04 Jul 2018", "12.03.2019 Amendment 2", "12.03.19 ...",
 * "2019.03 Administrative Change", "2.0 12 March 2019 Initial release". No
 * part of a section number is year-sized.
 *
 * Zero padding marks a date only in a three-part number ("12.03.19",
 * "15.06.20"): some sponsor templates number their sections "01 INTRODUCTION"
 * or "1.01 Background", and rejecting every padded part removed a whole
 * document's headings, citation index and section selection with them
 * (checker, 2026-10-19). The padded dates seen in real protocols ("04 Jul
 * 2018", "09 Aug 2021") carry a month name and are rejected by that.
 */
export function isDateShapedHeading(number: string, title: string): boolean {
  const parts = number.split('.');
  if (parts.length >= 3 && parts.some((p) => p.length > 1 && p.startsWith('0'))) return true;
  if (parts.some((p) => p.length >= 4)) return true;
  const t = title.trim();
  return MONTH_THEN_NUMBER.test(t) || TITLE_STARTS_WITH_DATE.test(t);
}

/**
 * THE VETO every reader of numbered lines shares: a line that can never be a
 * section heading, however its title is shaped. A date ("28 SEP 2017",
 * "12.03.2019 Amendment 2", see isDateShapedHeading) or a dot-leader TOC line
 * ("8.2.1 Demographics ........ 40"). Used here, and by the readers that
 * print or verify a section label: crf/crfProtocolReading (demographic
 * paragraphs), crf/crfProtocolTextLists (list sections) and
 * crf/protocolReview/sectionVerify (round 2, 2026-10-19).
 */
export function isNeverAHeadingLine(line: string): boolean {
  const t = String(line ?? '').replace(/\r$/, '').trim();
  if (LEADER_LINE.test(t)) return true;
  const m = /^(\d+(?:\.\d+)*)\.?[ \t]+(\S.*)$/.exec(t);
  return !!m && isDateShapedHeading(m[1], m[2]);
}

/**
 * For readers that work on an array of lines: which lines lie in the TOC
 * region (findTocRegion over the lines joined with "\n").
 */
export function tocLineFlags(lines: string[]): boolean[] {
  const toc = findTocRegion(lines.join('\n'));
  const flags: boolean[] = [];
  let pos = 0;
  for (const l of lines) { flags.push(inToc(pos, toc)); pos += l.length + 1; }
  return flags;
}

function scan(re: RegExp, src: string): HeadingCandidate[] {
  const out: HeadingCandidate[] = [];
  re.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(src)) !== null) {
    const title = m[2].trim();
    if (/\.$/.test(title)) continue;
    if (isNeverAHeadingLine(m[0])) continue;
    out.push({ number: m[1], title, start: m.index });
  }
  return out;
}

/**
 * The two ways a heading number is printed, read separately: a document uses
 * one style for its headings, and mixing them lets a numbered reference list
 * ("12. Smith J ...") stand in for real headings in the other style.
 */
export function headingCandidateSets(text: string): { plain: HeadingCandidate[]; dotted: HeadingCandidate[] } {
  const src = String(text ?? '');
  return { plain: scan(PLAIN_HEADING, src), dotted: scan(DOTTED_HEADING, src) };
}

// ── The numbering chain ────────────────────────────────────────────────────

const MAX_TOP_NUMBER = 40;
const MAX_COMPONENT = 60;
/** How far a heading number may jump past the previous one and still be the next heading. */
const MAX_STEP = 3;
/**
 * The highest number a NEW level may open at ("8.2" then "8.2.5" when 8.2.1-8.2.4
 * were not detected as headings, e.g. a title starting with a digit).
 */
const MAX_NEW_LEVEL = 10;

function parts(n: string): number[] {
  return n.split('.').map((x) => Number(x));
}

function inBounds(n: string): boolean {
  const p = parts(n);
  return p[0] <= MAX_TOP_NUMBER && p.every((x) => Number.isFinite(x) && x <= MAX_COMPONENT);
}

/** Could heading b directly follow heading a in a document's numbering? */
function follows(a: number[], b: number[]): boolean {
  const k = Math.min(a.length, b.length);
  for (let i = 0; i < k; i++) {
    if (b[i] === a[i]) continue;
    if (b[i] < a[i] || b[i] - a[i] > MAX_STEP) return false;
    return b.slice(i + 1).every((x) => x <= MAX_NEW_LEVEL);
  }
  if (b.length <= a.length) return false; // b equals a, or b is an ancestor of a
  return b.slice(a.length).every((x) => x <= MAX_NEW_LEVEL);
}

/**
 * The document's numbered headings as a CHAIN: the longest sequence of
 * candidates whose numbers could follow one another. A table row that happens
 * to look like a heading ("1 MBq/kg 0.25 MBq/kg", "211 At-MABG was ...")
 * cannot extend the chain and is dropped, so it does not split a real section.
 */
export function headingChain(entries: HeadingCandidate[]): HeadingCandidate[] {
  const ok = entries.filter((e) => inBounds(e.number));
  const n = ok.length;
  if (!n) return [];
  const p = ok.map((e) => parts(e.number));
  const len = new Array<number>(n).fill(1);
  const prev = new Array<number>(n).fill(-1);
  // Ties go to the LATER heading. A false heading (an undetected TOC line, a
  // numbered table row, a page footer) comes before the real heading it
  // imitates, so between two equally long numberings the later one is the
  // body's (checker, 2026-10-19: a TOC printed "1.1 Synopsis 6" tied the body
  // and won, and every body section fell into the last TOC line's span).
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < i; j++) {
      if (len[j] + 1 >= len[i] && follows(p[j], p[i])) { len[i] = len[j] + 1; prev[i] = j; }
    }
  }
  let best = 0;
  for (let i = 1; i < n; i++) if (len[i] >= len[best]) best = i;
  const out: HeadingCandidate[] = [];
  for (let i = best; i >= 0; i = prev[i]) out.push(ok[i]);
  return out.reverse();
}

/**
 * The document's numbered headings: the longer of the plain and dotted
 * chains, outside the TOC region. This is what protocolSpans cuts at.
 */
export function documentHeadingChain(text: string, toc: TextRange | null): HeadingCandidate[] {
  const { plain, dotted } = headingCandidateSets(text);
  return [plain, dotted]
    .map((set) => headingChain(set.filter((e) => !inToc(e.start, toc))))
    .reduce((a, b) => (b.length > a.length ? b : a));
}

/**
 * The headings a note may cite, in document order: the document's chain, plus
 * every multi-level candidate of either style that is off the chain, outside
 * the TOC, in bounds, and whose number the chain does not already hold (the
 * first such wins). See the header for why single numbers are not added.
 */
export function citationHeadings(text: string, toc: TextRange | null = findTocRegion(text)): HeadingCandidate[] {
  const chain = documentHeadingChain(text, toc);
  const held = new Set(chain.map((e) => e.number));
  const onChain = new Set(chain.map((e) => e.start));
  const { plain, dotted } = headingCandidateSets(text);
  const extras = [...plain, ...dotted]
    .filter((e) => !onChain.has(e.start) && !inToc(e.start, toc) && e.number.includes('.') && inBounds(e.number))
    .sort((a, b) => a.start - b.start)
    .filter((e) => (held.has(e.number) ? false : (held.add(e.number), true)));
  return [...chain, ...extras].sort((a, b) => a.start - b.start);
}
